import { signOut } from './supabase-client.js';
import { canViewExpenses, isOnline } from './store.js';
import { onConnectivityChange } from './utils/connectivity.js';
import {
  ensureTripContext,
  getContext,
  onTripChange,
  refreshLiveData,
  tearDownRealtime,
} from './trip-context.js';
import { normalizeMapDayKey, resolveDefaultMapDayKey } from './utils/map-default-day.js';
import { renderShelf } from './views/shelf.js';
import { refreshTripGroupFromContext, isTripGroupModalOpen } from './views/shell.js';
import {
  renderDayByDay,
  updateDayByDayMain,
} from './views/day-by-day.js';
import {
  renderChecklist,
  updateChecklistMain,
} from './views/checklist.js';
import {
  renderExpenses,
  updateExpensesMain,
} from './views/expenses.js';
import {
  renderJournal,
  updateJournalMain,
} from './views/journal.js';
import { renderPlanSection } from './views/plan-sections.js';
import { renderCustomize, updateCustomizeMain } from './views/customize.js';
import { renderMapView, updateMapViewMain, clearMapViewState } from './views/map-view.js';
import { renderWizardPlan, clearWizardPlanState } from './views/wizard-plan.js';
import { renderWizardInstance, clearWizardInstanceState } from './views/wizard-instance.js';
import { acceptTripInvite, setPendingInviteToken } from './trip-invites.js';

let container = null;
let currentRoute = null;
let routing = false;
let appInitialized = false;

function parseRoute() {
  const raw = location.hash.slice(1) || '/shelf';
  const path = raw.startsWith('/') ? raw : `/${raw}`;

  const tripMatch = path.match(/^\/trip\/([^/]+)(?:\/(.*))?$/);
  if (tripMatch) {
    const tripId = tripMatch[1];
    const rest = (tripMatch[2] || 'days').replace(/\/$/, '');
    if (rest === 'start') {
      return { name: 'tripStart', tripId };
    }
    const mapMatch = rest.match(/^map(?:\/(.+))?$/);
    if (mapMatch) {
      return {
        name: 'trip',
        tripId,
        view: 'map',
        mapDayKey: mapMatch[1] ?? null,
        sectionKey: null,
        customizeTab: null,
      };
    }

    const sectionMatch = rest.match(/^sections\/([^/]+)$/);
    if (sectionMatch) {
      return { name: 'trip', tripId, view: 'sections', sectionKey: sectionMatch[1], customizeTab: null };
    }
    const customizeMatch = rest.match(/^customize(?:\/(stays|wishlist|days))?$/);
    if (customizeMatch) {
      return {
        name: 'trip',
        tripId,
        view: 'customize',
        sectionKey: null,
        customizeTab: customizeMatch[1] ?? 'stays',
      };
    }
    const view = ['days', 'map', 'checklist', 'expenses', 'journal'].includes(rest) ? rest : 'days';
    return { name: 'trip', tripId, view, sectionKey: null, customizeTab: null };
  }

  if (path === '/' || path === '/shelf') {
    return { name: 'shelf' };
  }

  if (path === '/new/plan') {
    return { name: 'newPlan', tripId: null, step: 1 };
  }

  const newPlanMatch = path.match(/^\/new\/plan\/([^/]+)(?:\/(\d+))?$/);
  if (newPlanMatch) {
    return {
      name: 'newPlan',
      tripId: newPlanMatch[1],
      step: Math.min(5, Math.max(1, Number(newPlanMatch[2]) || 1)),
    };
  }

  const inviteMatch = path.match(/^\/invite\/([^/]+)$/);
  if (inviteMatch) {
    return { name: 'invite', token: inviteMatch[1] };
  }

  return { name: 'shelf' };
}

async function onLogout() {
  tearDownRealtime();
  await signOut();
}

function updateCurrentTripView() {
  const ctx = getContext();
  if (!ctx || !container) return;

  if (isTripGroupModalOpen(container)) {
    refreshTripGroupFromContext(container).catch(() => {});
  }

  switch (currentRoute?.view) {
    case 'days':
      updateDayByDayMain(container, ctx);
      break;
    case 'checklist':
      updateChecklistMain(container, ctx);
      break;
    case 'expenses':
      if (!canViewExpenses()) {
        location.hash = `#/trip/${currentRoute.tripId}/days`;
        return;
      }
      updateExpensesMain(container, ctx);
      break;
    case 'journal':
      updateJournalMain(container, ctx).catch(() => {});
      break;
    case 'map':
      updateMapViewMain(container, ctx);
      break;
    case 'customize':
      updateCustomizeMain(container, ctx, currentRoute?.customizeTab ?? 'stays');
      break;
    default:
      break;
  }
}

async function renderTrip(route) {
  const ctx = await ensureTripContext(route.tripId);

  switch (route.view) {
    case 'checklist':
      renderChecklist(container, ctx);
      break;
    case 'expenses':
      if (!canViewExpenses()) {
        location.hash = `#/trip/${route.tripId}/days`;
        return;
      }
      renderExpenses(container, ctx);
      break;
    case 'journal':
      renderJournal(container, ctx);
      break;
    case 'map': {
      if (route.mapDayKey === null) {
        const defaultKey = resolveDefaultMapDayKey(ctx);
        if (defaultKey) {
          location.hash = `#/trip/${route.tripId}/map/${defaultKey}`;
          return;
        }
      }
      const dayKey = normalizeMapDayKey(route.mapDayKey);
      const existingMap = container.querySelector('#trip-map-canvas');
      if (existingMap && getContext()?.tripId === route.tripId) {
        await updateMapViewMain(container, ctx, { dayKey });
      } else {
        await renderMapView(container, ctx, { dayKey });
      }
      break;
    }
    case 'sections':
      renderPlanSection(container, ctx, route.sectionKey);
      break;
    case 'customize':
      renderCustomize(container, ctx, route.customizeTab);
      break;
    case 'days':
    default:
      renderDayByDay(container, ctx);
      break;
  }
}

async function route() {
  if (!container || routing) return;
  routing = true;

  const routeInfo = parseRoute();
  const prevTripId = currentRoute?.tripId;
  currentRoute = routeInfo;

  try {
    if (routeInfo.name === 'invite') {
      setPendingInviteToken(routeInfo.token);
      try {
        const tripId = await acceptTripInvite(routeInfo.token);
        location.hash = `#/trip/${tripId}/days`;
        return;
      } catch (error) {
        container.innerHTML = `
          <main class="app-loading">
            <h1>Metis</h1>
            <p class="status-error">${error.message}</p>
            <a class="btn-primary" href="#/shelf">Torna allo scaffale</a>
          </main>
        `;
        return;
      }
    }

    if (routeInfo.name === 'shelf') {
      tearDownRealtime();
      clearWizardPlanState();
      clearWizardInstanceState();
      clearMapViewState();
      await renderShelf(container, { onLogout });
      return;
    }

    if (routeInfo.name === 'newPlan') {
      tearDownRealtime();
      await renderWizardPlan(container, { tripId: routeInfo.tripId, step: routeInfo.step });
      return;
    }

    if (routeInfo.name === 'tripStart') {
      tearDownRealtime();
      await renderWizardInstance(container, routeInfo.tripId);
      return;
    }

    if (routeInfo.name === 'trip') {
      if (prevTripId && prevTripId !== routeInfo.tripId) {
        tearDownRealtime();
        clearMapViewState();
      }
      await renderTrip(routeInfo);
    }
  } catch (error) {
    if (error.code === 'INSTANCE_REQUIRED' && error.tripId) {
      location.hash = `#/trip/${error.tripId}/start`;
      return;
    }
    container.innerHTML = `
      <main class="app-loading">
        <h1>Metis</h1>
        <p class="status-error">${error.message}</p>
        <a class="btn-primary" href="#/shelf">Torna allo scaffale</a>
      </main>
    `;
  } finally {
    routing = false;
  }
}

export function startApp(root) {
  container = root;

  if (!appInitialized) {
    appInitialized = true;
    onTripChange(async () => {
      await refreshLiveData({ forceNetwork: true });
      updateCurrentTripView();
    });
    window.addEventListener('hashchange', route);
    onConnectivityChange(() => {
      if (isOnline()) route();
      else updateCurrentTripView();
    });
    window.addEventListener('online', () => route());
    window.addEventListener('offline', () => updateCurrentTripView());
  }

  if (!location.hash || location.hash === '#/' || location.hash === '#') {
    location.hash = '#/shelf';
  }

  route();
}
